import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UserCog, X } from "lucide-react";

/**
 * Gap-closure item 4: lightweight booking staff assignment ("who's handling
 * this booking"). Deliberately small - one dropdown, one audit trail - and
 * distinct from DriverAssignmentPanel (who physically drives a with_driver
 * rental): staff assignment is general operational ownership, always
 * available regardless of rental_service_type.
 */
export default function StaffAssignmentPanel({ bookingId, data, onChanged }) {
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/bookings/staff-options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const assign = async (staffId) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/assign-staff`, { staff_id: staffId || null });
      toast.success(staffId ? "Staff assigned" : "Assignment cleared");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="staff-assignment-panel">
      <h2 className="font-display text-lg font-semibold flex items-center gap-2">
        <UserCog className="w-5 h-5" /> Assigned Staff
      </h2>
      {data.assigned_staff_id ? (
        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm font-medium" data-testid="assigned-staff-name">{data.assigned_staff_name || "Assigned"}</span>
          <button
            onClick={() => assign(null)}
            disabled={busy}
            data-testid="clear-staff-assignment-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50 disabled:opacity-50"
          >
            <X className="w-3 h-3" /> Change assignment
          </button>
        </div>
      ) : (
        <p className="mt-2 text-sm text-slate-500">Not yet assigned to anyone.</p>
      )}
      <select
        value=""
        onChange={(e) => { if (e.target.value) assign(e.target.value); }}
        disabled={busy || options.length === 0}
        data-testid="staff-assignment-select"
        className="mt-3 w-full sm:w-auto px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm disabled:opacity-50"
      >
        <option value="">{data.assigned_staff_id ? "Reassign to…" : "Assign Staff…"}</option>
        {options.filter((o) => o.id !== data.assigned_staff_id).map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}
